import { supabase } from "@/integrations/supabase/client";

/**
 * Unified XP Update Utility
 * Updates XP across ALL relevant tables:
 * - leaderboard (daily_xp, weekly_xp, total_xp)
 * - user_progress (current_xp, current_level)
 * - weekly_progress (via RPC)
 * 
 * Call this whenever a user earns XP from missions, quizzes, or projects
 */
export async function updateXPAcrossAllTables(
  userId: string,
  xpAmount: number,
  incrementMissions: boolean = true
): Promise<{ success: boolean; newTotalXp: number; newLevel: number }> {
  if (!userId || xpAmount <= 0) {
    return { success: false, newTotalXp: 0, newLevel: 1 };
  }

  try {
    // Get user profile for username (needed for leaderboard)
    const { data: profile } = await supabase
      .from("profiles")
      .select("display_name, username")
      .eq("id", userId)
      .single();

    const username = profile?.display_name || profile?.username || "User";

    // 1. Update LEADERBOARD table (daily, weekly, total XP)
    const { data: existingLeaderboard } = await supabase
      .from("leaderboard")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    let newTotalXp = xpAmount;
    let newLevel = 1;

    if (existingLeaderboard) {
      newTotalXp = (existingLeaderboard.total_xp || 0) + xpAmount;
      newLevel = Math.floor(newTotalXp / 500) + 1;
      
      // Calculate new daily/weekly XP, ensuring they never exceed total
      const newDailyXp = Math.min((existingLeaderboard.daily_xp || 0) + xpAmount, newTotalXp);
      const newWeeklyXp = Math.min((existingLeaderboard.weekly_xp || 0) + xpAmount, newTotalXp);
      
      const updateData: any = {
        total_xp: newTotalXp,
        weekly_xp: newWeeklyXp,
        daily_xp: newDailyXp,
        level: newLevel,
        updated_at: new Date().toISOString(),
      };
      
      if (incrementMissions) {
        updateData.missions_completed = (existingLeaderboard.missions_completed || 0) + 1;
      }

      await supabase
        .from("leaderboard")
        .update(updateData)
        .eq("user_id", userId);
    } else {
      // Create new leaderboard entry
      newLevel = 1;
      await supabase.from("leaderboard").insert({
        user_id: userId,
        username,
        total_xp: xpAmount,
        weekly_xp: xpAmount,
        daily_xp: xpAmount,
        level: 1,
        missions_completed: incrementMissions ? 1 : 0,
        streak_days: 1,
      });
    }

    // 2. Update USER_PROGRESS table
    const { data: existingProgress } = await supabase
      .from("user_progress")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (existingProgress) {
      const currentXp = (existingProgress.current_xp || 0) + xpAmount;
      newLevel = Math.floor(currentXp / 500) + 1;
      newTotalXp = currentXp;
      
      const updateData: any = {
        current_xp: currentXp,
        current_level: newLevel,
        last_activity_date: new Date().toISOString().split("T")[0],
        updated_at: new Date().toISOString(),
      };
      
      if (incrementMissions) {
        updateData.total_missions_completed = (existingProgress.total_missions_completed || 0) + 1;
      }

      await supabase
        .from("user_progress")
        .update(updateData)
        .eq("user_id", userId);
    } else {
      await supabase.from("user_progress").insert({
        user_id: userId,
        current_xp: xpAmount,
        current_level: 1,
        total_missions_completed: incrementMissions ? 1 : 0,
      });
    }

    // 3. Update weekly_progress table via RPC (for weekly graphs)
    try {
      await supabase.rpc("update_daily_xp", {
        p_user_id: userId,
        p_xp_amount: xpAmount,
      });
    } catch (rpcError) {
      console.error("RPC update_daily_xp failed:", rpcError);
    }

    console.log(`✅ XP updated: +${xpAmount} XP, Total: ${newTotalXp}, Level: ${newLevel}`);
    
    return { success: true, newTotalXp, newLevel };
  } catch (error) {
    console.error("Error updating XP across tables:", error);
    return { success: false, newTotalXp: 0, newLevel: 1 };
  }
}

/**
 * Update skill-specific progress
 */
export async function updateSkillProgress(
  userId: string,
  skillId: string,
  missionId: string,
  sectionId: string,
  xpEarned: number,
  totalMissionsCompleted: number,
  totalMissionsInSkill: number
): Promise<boolean> {
  try {
    // Record lesson completion
    // IMPORTANT: lesson_completions has no UPDATE policy.
    // We use upsert with ignoreDuplicates so it behaves like INSERT ... ON CONFLICT DO NOTHING
    // (no UPDATE is attempted, so RLS is respected).
    const { error: completionError } = await supabase
      .from("lesson_completions")
      .upsert(
        [
          {
            user_id: userId,
            skill_id: skillId,
            module_id: sectionId,
            lesson_id: missionId,
            xp_earned: xpEarned,
          },
        ],
        { onConflict: "user_id,skill_id,lesson_id", ignoreDuplicates: true }
      );

    if (completionError) throw completionError;

    // Update skill progress
    const progressPercent = Math.min(100, Math.floor((totalMissionsCompleted / totalMissionsInSkill) * 100));
    
    await supabase.from("skill_progress").upsert({
      user_id: userId,
      skill_id: skillId,
      lessons_completed: totalMissionsCompleted,
      progress_percentage: progressPercent,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id,skill_id" });

    return true;
  } catch (error) {
    console.error("Error updating skill progress:", error);
    return false;
  }
}
